import { Box, Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import React, { useCallback, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { SmartChargeSettingsFormModel } from 'src/Libs/smart-charge/models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NumberField } from 'src/Libs/inputs/formik'
import { UnitTypes } from 'src/Libs/types'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { addDays, format, isBefore } from '@e3dc-react/shell/Libs/date'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  buttonsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}))

interface Props {
  isActive: boolean
  max: number
  min: number
  /**
   * lastChargeStartTime can be a negative number or a timestamp.
   * A negative number means the manual charging can be activated. A timestamp tells you the last time the feature has been activated.
   * If the timestamp is older than 24h that also means the feature can be activated again.
   */
  lastChargeStartTime: number
  hasWritingRights?: boolean
}

const FIELD_NAME = 'emsStartManualCharge'
const VALUE_MEANS_DISABLED = 0
const DAYS_AFTER_WHICH_CHARGE_IS_ALLOWED_AGAIN = 1

/**
 * TODO: Consider to pass the following props separately from a higher component (e.g. page) to better fit to the atom-design-principle:
 * (1) disabled (2) displayTooltip (3) titleKey
 * note: currently (1) and (2) both depend on hasWritingRights, which should be separated
 */
const ChargePowerSettingsForm: React.VFC<Props> = ({ isActive, min, max, lastChargeStartTime, hasWritingRights = true }) => {
  const { classes } = useStyles()
  const { breakpoints } = useTheme()
  const { t } = useCustomTranslation()
  const { handleSubmit, setFieldValue } = useFormikContext<SmartChargeSettingsFormModel>()
  const [inputField] = useField<number>(FIELD_NAME)
  const isDisabled = useMemo(() => (min <= 0 && max <= 0) || max <= min, [min, max])
  const chargeAvailableAgainTime = useMemo(() => {
    if (lastChargeStartTime <= 0) {
      return new Date(0)
    }
    return addDays(new Date(lastChargeStartTime), DAYS_AFTER_WHICH_CHARGE_IS_ALLOWED_AGAIN)
  }, [lastChargeStartTime])

  const hideBanner = isActive || (!isActive && lastChargeStartTime < 0) || (!isActive && isBefore(chargeAvailableAgainTime, new Date()))

  const maxChargePowerInputs = (
    <Stack sx={{ margin: { xs: 'auto', sm: 0 } }} direction={{ sm: 'row', xs: 'column' }} alignItems="center" gap={2}>
      <Box>
        {min} {UnitTypes.wattHour}
      </Box>
      <NumberField
        min={min}
        max={max}
        disabled={isDisabled || !hasWritingRights || isActive}
        label={t('systemSettings.smartFunctions.smartCharge.chargePower.maxChargePower.label')}
        name="emsStartManualCharge"
        suffix={UnitTypes.wattHour}
      />
      <Box>
        {max} {UnitTypes.wattHour}
      </Box>
    </Stack>
  )

  const isStartDisabled = useMemo(() => {
    const isChargingAvailable = lastChargeStartTime < 0 || isBefore(chargeAvailableAgainTime, new Date())

    if (isActive || !isChargingAvailable) {
      return true
    }
    return Number(inputField.value) === VALUE_MEANS_DISABLED || Number(inputField.value) > max
  }, [inputField.value, isActive, max, lastChargeStartTime, chargeAvailableAgainTime])

  const fullWidthButtons = useMediaQuery(breakpoints.down('md'))

  const isStopDisabled = useMemo(() => {
    if (isActive) {
      return false
    }
    return true
  }, [isActive])

  const handleStart = useCallback(() => {
    setFieldValue(FIELD_NAME, Number(inputField.value))
    handleSubmit()
  }, [handleSubmit, setFieldValue, inputField])

  const handleStop = useCallback(() => {
    setFieldValue(FIELD_NAME, VALUE_MEANS_DISABLED)
    handleSubmit()
  }, [handleSubmit, setFieldValue])

  return (
    <form onSubmit={handleSubmit}>
      <Box pb={2}>
        <Alert
          severity="warning"
          text={t('systemSettings.smartFunctions.smartCharge.chargePower.maxChargePower.next-start-time', {
            value: format(chargeAvailableAgainTime, t('common.format.date-fns-date-time')),
          })}
          hide={hideBanner}
        />
      </Box>
      <Stack margin={4} marginBottom={2} marginTop={2}>
        <SettingsBox
          title={t('systemSettings.smartFunctions.smartCharge.chargePower.maxChargePower.label')}
          suffix={maxChargePowerInputs}
          disabled={!hasWritingRights}
          description={t('systemSettings.smartFunctions.smartCharge.maxChargePower.form.maxChargePowerDescription')}
        />
      </Stack>
      <Stack className={classes.buttonsWrapper}>
        <Button
          disabled={isStartDisabled || !hasWritingRights}
          type="button"
          variant="contained"
          size="large"
          color="primary"
          onClick={handleStart}
          fullWidth={fullWidthButtons}
        >
          {t('systemSettings.smartFunctions.smartCharge.chargePower.maxChargePower.form.startControl')}
        </Button>
        <Button
          disabled={isStopDisabled || !hasWritingRights}
          type="button"
          variant="contained"
          size="large"
          color="error"
          onClick={handleStop}
          fullWidth={fullWidthButtons}
        >
          {t('systemSettings.smartFunctions.smartCharge.chargePower.maxChargePower.form.stopControl')}
        </Button>
      </Stack>
    </form>
  )
}

export default ChargePowerSettingsForm
